import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useDisclosure } from "@mantine/hooks";
import {
  Button,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import BackButton from "../../../../components/View/BackButton";
import {
  IconChevronDown,
  IconInfoCircle,
  IconUserSearch,
} from "@tabler/icons-react";
import {
  enrichList,
  enrichListByExternalApi,
  enrichWithLinkedin,
  findList,
  generateLinkedin,
} from "../../../../api/ContactAPI";
import { notifications } from "@mantine/notifications";
import LinkedinAccountSelector from "../../../../components/Selectors/LinkedinAccountSelector";
import { transformFilterToServer } from "../../../../functions/utilFunctions";
import { getAIKeys } from "../../../../api/ConfigsApi";
import { useConfigStore } from "../../../../stores/ConfigStore";

const ListEnrich = ({
  current,
  onFinish,
  closeMenu,
  inTable,
  selectedIds,
  filter,
  filterResultNumber,
  total
}) => {
  const lang = useLanguage((s) => s.language);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [linkedin, setLinkedin] = useState(false);
  const [account, setAccount] = useState(null);
  const [withoutEmail, setWithoutEmail] = useState(true);
  const [list, setList] = useState(current);
  const [enrichData, setEnrichData] = useState("all");

  const handleSubmit = async (e) => {
    e.preventDefault();

    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    setLoading(true);

    try {
      if (withoutEmail || data.generate_email === "on") {
        await enrichList({
          contact_list_id: current.id,
          generate_email: data.generate_email === "on" ? true : false,
          contact_ids:
            enrichData === "selection_inverse"
              ? []
              : enrichData === "selection"
                ? selectedIds
                : [],
          excluded_contact_ids: enrichData === "selection_inverse" ? selectedIds : [],
          filter:
            enrichData === "selection"
              ? { mode: "or", values: [] }
              : filter?.items?.length > 0 && enrichData !== "selection"
                ? transformFilterToServer(filter)
                : { mode: "or", values: [] },
        });
      }
      if (data.generate_linkedin === "on") {
        await generateLinkedin({ contact_list_id: current.id });
      }
      if (linkedin) {
        await enrichWithLinkedin({
          contact_list_id: current.id,
          linkedin_account_id: account,
        });
      }
      if (data?.dropcontact) {
        await enrichListByExternalApi({
          contact_list_id: current.id,
          external_api_key_id: parseInt(data.dropcontact),
          enrich_external_api: "dropcontact",
          filter: { mode: "or", values: [] }
        });
      }
      if (data?.contactout) {
        await enrichListByExternalApi({
          contact_list_id: current.id,
          external_api_key_id: parseInt(data.contactout),
          enrich_external_api: "contactout",
          filter: { mode: "or", values: [] }
        });
      }
    } catch (error) {
      setLoading(false);
    }

    notifications.show({
      message: lang.contact_lists.enrich.success,
      color: "tertiary.8",
    });
    setDone(true);
    setLoading(false);
  };

  const handleOpen = () => {
    setOpened(true);
    setLoading(true);
    findList(current.id)
      .then((r) => {
        setList(r.data.contact_list_profile);
      })
      .finally(() => setLoading(false));
  };

  const handleClose = () => {
    setOpened(false)
    closeMenu?.();
    setDone(false);
    if (done) {
      onFinish?.();
    }
  };

  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const aiKeys = useConfigStore(s => s.externalKeys)

  const [useDropcontact, setUseDropcontact] = useState(false);
  const dropcontactKeys = useMemo(() => {
    return aiKeys.filter(k => k.type === "dropcontact")
  }, [aiKeys])

  const [useContactOut, setUseContactOut] = useState(false);
  const contactoutKeys = useMemo(() => {
    return aiKeys.filter(k => k.type === "contactout")
  }, [aiKeys])

  useEffect(() => {
    if (opened) {
      setLoadingApiKeys(true)
      getAIKeys().finally(() => setLoadingApiKeys(false))
    }
  }, [opened])

  return (
    <>
      {inTable ? (
        <Button
          className="to-blue"
          variant="light"
          justify="start"
          rightSection={<IconChevronDown size={16} />}
          leftSection={<IconUserSearch size={18} />}
          styles={{
            label: {
              marginRight: "auto",
            },
          }}
          onClick={handleOpen}
        >
          {lang.contact_lists.enrich.label}
        </Button>
      ) : (
        <Button
          onClick={handleOpen}
          leftSection={<IconUserSearch size={18} />}
          color="primary"
          variant="subtle"
          justify="start"
        >
          {lang.contact_lists.enrich.label}
        </Button>
      )}

      {opened && (
        <Modal
          opened={opened}
          size={"xl"}
          onClose={handleClose}
          title={
            <>
              {lang.contact_lists.enrich.dialog.title}{" "}
              <b>
                {current?.name} #{current.id}
              </b>
            </>
          }
        >
          <LoadingOverlay visible={loading || loadingApiKeys} />
          {done ? (
            <Stack>
              <Text ta={"center"}>
                {lang.contact_lists.enrich.dialog.success}
              </Text>
              <Group justify="center" mt={"md"}>
                <Button
                  onClick={() => {
                    handleClose();
                    onFinish?.();
                  }}
                >
                  {lang.global.finish}
                </Button>
              </Group>
            </Stack>
          ) : (
            <Stack component={"form"} onSubmit={handleSubmit} gap={"md"}>
              <Text c={"dimmed"}>{lang.contact_lists.enrich.dialog.text}</Text>

              <Switch
                label={lang.contact_lists.enrich.dialog.default}
                checked={withoutEmail}
                size="md"
                onChange={(e) => setWithoutEmail(e.target.checked)}
              />

              <Switch
                label={lang.contact_lists.enrich.dialog.generate_email}
                name="generate_email"
                size="md"
              />

              {
                inTable &&
                <SegmentedControl
                  data={[
                    {
                      label: lang.contact_lists.enrich_mobile.all_list + ` ${total ? ` (${total})` : ""}`,
                      value: "all",
                    },
                    {
                      label: lang.contact_lists.export_contacts.filter + `${filter?.items?.length > 0 ? ` (${filterResultNumber})` : ""}`,
                      value: "filter",
                      disabled: filter?.items?.length === 0,
                    },
                    {
                      label: lang.contact_lists.export_contacts.selection + `${selectedIds?.length > 0 ? ` (${selectedIds.length})` : ""}`,
                      value: "selection",
                      disabled: selectedIds.length === 0,
                    },
                    {
                      label: lang.contact_lists.export_contacts.all_but_selection + `${selectedIds?.length > 0
                        ? ` (${filterResultNumber - selectedIds.length})`
                        : ""}`,
                      value: "selection_inverse",
                      disabled: selectedIds.length === 0,
                    },
                  ]}
                  value={enrichData}
                  onChange={setEnrichData}
                />
              }

              <Divider opacity={.5} />

              <Group>
                <Switch
                  label={lang.contact_lists.enrich.dialog.generate_linkedin}
                  name="generate_linkedin"
                  size="md"
                />

                <Tooltip
                  label={lang.contact_lists.enrich.dialog.generate_linkedin_tip}
                >
                  <ThemeIcon variant="light" size={"md"} radius={"xl"}>
                    <IconInfoCircle />
                  </ThemeIcon>
                </Tooltip>
              </Group>

              <Group>
                <Switch
                  label={lang.contact_lists.enrich.dialog.enrich_linkedin}
                  checked={linkedin}
                  onChange={(e) => setLinkedin(e.target.checked)}
                  name="enrich_linkedin"
                  size="md"
                  disabled={list.number_of_linkedin_url === 0}
                />

                <Tooltip
                  label={lang.contact_lists.enrich.dialog.enrich_linkedin_tip}
                >
                  <ThemeIcon variant="light" size={"md"} radius={"xl"}>
                    <IconInfoCircle />
                  </ThemeIcon>
                </Tooltip>
              </Group>

              {list.number_of_linkedin_url === 0 && (
                <Text c={"red"} mt={-12} size="sm">
                  {lang.contact_lists.enrich.dialog.no_linkedin_contact}
                </Text>
              )}

              {linkedin && (
                <LinkedinAccountSelector
                  label={
                    lang.contact_lists.convert_linkedin_links.dialog
                      .linkedin_account
                  }
                  required
                  value={account}
                  onChange={(v) => setAccount(v?.id || null)}
                  size="md"
                  withPool={false}
                />
              )}

              {
                (dropcontactKeys.length > 0 || contactoutKeys.length > 0) && (
                  <Divider opacity={.5} />
                )
              }
              {
                dropcontactKeys.length > 0 &&
                <Group>
                  <Switch
                    label={lang.contact_lists.enrich.dialog.enrich_dropcontact}
                    checked={useDropcontact}
                    onChange={(e) => {
                      setUseDropcontact(e.target.checked)
                      // setUseContactOut(false)
                    }}
                    size="md"
                    w={250}
                  />
                  {useDropcontact &&
                    <Select data={dropcontactKeys.map(item => ({ label: item.name, value: item.id.toString() }))} name="dropcontact" defaultValue={dropcontactKeys?.[0]?.id?.toString()} />
                  }
                </Group>
              }

              {
                contactoutKeys.length > 0 &&
                <Group>
                  <Switch
                    label={lang.contact_lists.enrich.dialog.enrich_contactout}
                    checked={useContactOut}
                    onChange={(e) => {
                      setUseContactOut(e.target.checked)
                      // setUseDropcontact(false)
                    }}
                    size="md"
                    w={250}
                  />
                  {useContactOut &&
                    <Select data={contactoutKeys.map(item => ({ label: item.name, value: item.id.toString() }))} name="contactout" defaultValue={contactoutKeys?.[0]?.id?.toString()} />
                  }
                </Group>
              }

              <Group justify="end" mt={"xl"}>
                <BackButton onClick={handleClose} size="md" />

                <Button
                  leftSection={<IconUserSearch size={18} />}
                  loading={loading}
                  type="submit"
                  size="md"
                  disabled={list.number_of_contacts === 0}
                >
                  {lang.contact_lists.enrich.dialog.submit_button}
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      )}
    </>
  );
};

export default ListEnrich;
